import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';

const LABELS = {
  hot: { label: '🔥 Горячий', color: '#e74c3c', bg: '#fdecea' },
  warm: { label: '👍 Тёплый', color: '#e67e22', bg: '#fef5e7' },
  cold: { label: '❄️ Холодный', color: '#3498db', bg: '#ebf5fb' },
  refused: { label: '✗ Отказ', color: '#95a5a6', bg: '#f2f3f4' },
  deal: { label: '✅ Сделка', color: '#27ae60', bg: '#eafaf1' },
};

export default function Conversations() {
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaCaption, setMediaCaption] = useState('');
  const [filterLabel, setFilterLabel] = useState('');
  const [showLabelMenu, setShowLabelMenu] = useState(false);
  const [newQR, setNewQR] = useState({ title: '', text: '' });
  const [showNewQR, setShowNewQR] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const bottomRef = useRef();
  const textRef = useRef();
  const prevConvsRef = useRef([]);
  const mediaRef = useRef();

  const loadConvs = async () => {
    const url = filterLabel ? `/conversations?label=${filterLabel}` : '/conversations';
    const { data } = await api.get(url);

    // Уведомления о новых сообщениях
    const prevIds = new Set(prevConvsRef.current.map(c => c.id));
    const newConvs = data.filter(c => !prevIds.has(c.id));
    if (newConvs.length > 0 && prevConvsRef.current.length > 0) {
      setUnreadCount(n => n + newConvs.length);
      if (document.hidden) {
        document.title = `(${unreadCount + newConvs.length}) AgentDesk`;
      }
      // Звуковое уведомление
      try {
        const ctx = new AudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 800; gain.gain.value = 0.1;
        osc.start(); osc.stop(ctx.currentTime + 0.15);
      } catch {}
    }
    prevConvsRef.current = data;
    setConvs(data);
  };

  const loadMessages = async (convId) => {
    const { data } = await api.get(`/conversations/${convId}/messages`);
    setMessages(data);
  };

  useEffect(() => {
    loadConvs();
    api.get('/accounts').then(r => setAccounts(r.data));
    api.get('/quick-replies').then(r => setQuickReplies(r.data));
    const t = setInterval(loadConvs, 3000);
    return () => clearInterval(t);
  }, [filterLabel]);

  useEffect(() => {
    if (!selected) return;
    const t = setInterval(() => loadMessages(selected.id), 3000);
    return () => clearInterval(t);
  }, [selected?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Сбрасываем unread при открытии вкладки
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) { setUnreadCount(0); document.title = 'AgentDesk'; } };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const openConv = async (c) => {
    setSelected(c); setShowLabelMenu(false); setShowQR(false); setShowMedia(false);
    await loadMessages(c.id);
    setText('');
    setTimeout(() => textRef.current?.focus(), 100);
  };

  const sendText = async () => {
    if (!text.trim() || !selected) return;
    setSending(true);
    try {
      await api.post(`/conversations/${selected.id}/send`, {
        text, account_id: selected.account_id, external_user_id: selected.external_user_id
      });
      setText(''); await loadMessages(selected.id);
    } catch (e) { alert(e.response?.data?.detail || 'Ошибка отправки'); }
    setSending(false);
  };

  const sendMedia = async () => {
    if (!mediaFile || !selected) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append('file', mediaFile);
      form.append('account_id', selected.account_id);
      form.append('external_user_id', selected.external_user_id);
      form.append('caption', mediaCaption);
      await api.post(`/conversations/${selected.id}/send-media`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowMedia(false); setMediaFile(null); setMediaCaption('');
      await loadMessages(selected.id);
    } catch (e) { alert(e.response?.data?.detail || 'Ошибка'); }
    setSending(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); }
  };

  const toggleEscalate = async () => {
    await api.post(`/conversations/${selected.id}/escalate`, { is_escalated: !selected.is_escalated });
    const updated = { ...selected, is_escalated: !selected.is_escalated };
    setSelected(updated); setConvs(cs => cs.map(c => c.id === updated.id ? updated : c));
  };

  const setLabel = async (label) => {
    const newLabel = selected.label === label ? null : label;
    await api.post(`/conversations/${selected.id}/label`, { label: newLabel });
    const updated = { ...selected, label: newLabel };
    setSelected(updated); setConvs(cs => cs.map(c => c.id === updated.id ? updated : c));
    setShowLabelMenu(false);
  };

  const useQuickReply = (qr) => { setText(qr.text); setShowQR(false); textRef.current?.focus(); };

  const saveQR = async () => {
    if (!newQR.title || !newQR.text) return;
    const { data } = await api.post('/quick-replies', newQR);
    setQuickReplies(qrs => [...qrs, data]);
    setNewQR({ title: '', text: '' }); setShowNewQR(false);
  };

  const deleteQR = async (id) => {
    await api.delete(`/quick-replies/${id}`);
    setQuickReplies(qrs => qrs.filter(q => q.id !== id));
  };

  const fmt = (dt) => dt ? new Date(dt).toLocaleString('ru', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  const displayName = (c) => c?.external_first_name || (c?.external_username ? `@${c.external_username}` : `ID: ${c?.external_user_id}`);
  const getAccount = (c) => accounts.find(a => a.id === c?.account_id);

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Список диалогов */}
      <div style={{ width: 280, borderRight: '1px solid #e8e8e6', display: 'flex', flexDirection: 'column', background: '#fff', flexShrink: 0 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e8e8e6' }}>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>
            Диалоги <span style={{ fontSize: 12, color: '#aaa', fontWeight: 400 }}>({convs.length})</span>
            {unreadCount > 0 && <span style={{ marginLeft: 8, background: '#e74c3c', color: '#fff', fontSize: 11, padding: '1px 7px', borderRadius: 10 }}>{unreadCount}</span>}
          </div>
          {/* Фильтр по меткам */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button onClick={() => setFilterLabel('')} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 8, border: '1px solid', borderColor: !filterLabel ? '#534AB7' : '#e8e8e6', background: !filterLabel ? '#f0effe' : '#fff', color: !filterLabel ? '#534AB7' : '#888', cursor: 'pointer' }}>Все</button>
            {Object.entries(LABELS).map(([key, l]) => (
              <button key={key} onClick={() => setFilterLabel(filterLabel === key ? '' : key)}
                style={{ fontSize: 11, padding: '3px 8px', borderRadius: 8, border: '1px solid', borderColor: filterLabel === key ? l.color : '#e8e8e6', background: filterLabel === key ? l.bg : '#fff', color: filterLabel === key ? l.color : '#888', cursor: 'pointer' }}>
                {l.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {convs.length === 0 && <div style={{ padding: 24, color: '#aaa', fontSize: 13, textAlign: 'center' }}>Диалогов нет</div>}
          {convs.map(c => {
            const acc = getAccount(c);
            const lbl = c.label ? LABELS[c.label] : null;
            return (
              <div key={c.id} onClick={() => openConv(c)}
                style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0ee', cursor: 'pointer', background: selected?.id === c.id ? '#f0effe' : 'transparent' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{displayName(c)}</div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {lbl && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 6, background: lbl.bg, color: lbl.color }}>{lbl.label}</span>}
                    {c.is_escalated && <span style={{ fontSize: 10, background: '#fef3cd', color: '#856404', padding: '1px 5px', borderRadius: 6 }}>👤</span>}
                  </div>
                </div>
                {acc && <div style={{ fontSize: 11, color: '#534AB7', marginTop: 1 }}>через {acc.first_name || acc.name}</div>}
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>{fmt(c.updated_at)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Окно диалога */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f8f8f7', minWidth: 0 }}>
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 14 }}>Выберите диалог</div>
        ) : (
          <>
            {/* Шапка */}
            <div style={{ padding: '10px 16px', background: '#fff', borderBottom: '1px solid #e8e8e6', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{displayName(selected)}</div>
                <div style={{ fontSize: 12, color: '#aaa' }}>
                  {getAccount(selected) && <span style={{ color: '#534AB7' }}>через {getAccount(selected).first_name || getAccount(selected).name} · </span>}
                  ID: {selected.external_user_id}
                </div>
              </div>

              {/* Метка */}
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowLabelMenu(!showLabelMenu)}
                  style={{ padding: '5px 12px', border: '1px solid', borderColor: selected.label ? LABELS[selected.label]?.color : '#e8e8e6', borderRadius: 8, fontSize: 12, cursor: 'pointer', background: selected.label ? LABELS[selected.label]?.bg : '#fff', color: selected.label ? LABELS[selected.label]?.color : '#888' }}>
                  {selected.label ? LABELS[selected.label]?.label : '🏷 Метка'}
                </button>
                {showLabelMenu && (
                  <div style={{ position: 'absolute', top: 34, left: 0, background: '#fff', border: '1px solid #e8e8e6', borderRadius: 8, zIndex: 50, minWidth: 140, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    <div onClick={() => setLabel(null)} style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: '#aaa' }}>✕ Убрать метку</div>
                    {Object.entries(LABELS).map(([key, l]) => (
                      <div key={key} onClick={() => setLabel(key)}
                        style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: l.color, background: selected.label === key ? l.bg : 'transparent' }}>
                        {l.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button onClick={toggleEscalate}
                style={{ padding: '5px 12px', border: '1px solid', borderRadius: 8, fontSize: 12, cursor: 'pointer', background: 'none', borderColor: selected.is_escalated ? '#27ae60' : '#e67e22', color: selected.is_escalated ? '#27ae60' : '#e67e22' }}>
                {selected.is_escalated ? '🤖 Вернуть ИИ' : '👤 Оператору'}
              </button>
            </div>

            {/* Сообщения */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
              {messages.map(m => (
                <div key={m.id} style={{ display: 'flex', justifyContent: m.role === 'assistant' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                  <div style={{ maxWidth: '70%', padding: '9px 13px', borderRadius: m.role === 'assistant' ? '12px 12px 2px 12px' : '12px 12px 12px 2px', background: m.role === 'assistant' ? '#534AB7' : '#fff', color: m.role === 'assistant' ? '#fff' : '#333', fontSize: 14, border: m.role === 'user' ? '1px solid #e8e8e6' : 'none', lineHeight: 1.5, wordBreak: 'break-word' }}>
                    {m.content}
                    <div style={{ fontSize: 10, opacity: 0.6, marginTop: 3, textAlign: 'right' }}>{m.role === 'assistant' ? '🤖 ' : ''}{fmt(m.created_at)}</div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Быстрые ответы */}
            {showQR && (
              <div style={{ background: '#fff', borderTop: '1px solid #e8e8e6', padding: '10px 14px', maxHeight: 200, overflowY: 'auto' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                  Быстрые ответы
                  <button onClick={() => setShowNewQR(!showNewQR)} style={{ fontSize: 11, color: '#534AB7', background: 'none', border: 'none', cursor: 'pointer' }}>+ добавить</button>
                </div>
                {showNewQR && (
                  <div style={{ marginBottom: 10, display: 'flex', gap: 6 }}>
                    <input placeholder="Название" value={newQR.title} onChange={e => setNewQR(q => ({ ...q, title: e.target.value }))} style={{ flex: 1, padding: '5px 8px', border: '1px solid #e8e8e6', borderRadius: 6, fontSize: 12, outline: 'none' }} />
                    <input placeholder="Текст" value={newQR.text} onChange={e => setNewQR(q => ({ ...q, text: e.target.value }))} style={{ flex: 2, padding: '5px 8px', border: '1px solid #e8e8e6', borderRadius: 6, fontSize: 12, outline: 'none' }} />
                    <button onClick={saveQR} style={{ padding: '5px 10px', background: '#534AB7', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Сохранить</button>
                  </div>
                )}
                {quickReplies.length === 0 && <div style={{ color: '#aaa', fontSize: 12 }}>Нет быстрых ответов</div>}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {quickReplies.map(qr => (
                    <div key={qr.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button onClick={() => useQuickReply(qr)} style={{ padding: '5px 10px', background: '#f0effe', color: '#534AB7', border: '1px solid #d4d0f5', borderRadius: 8, fontSize: 12, cursor: 'pointer' }} title={qr.text}>
                        {qr.title}
                      </button>
                      <button onClick={() => deleteQR(qr.id)} style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 12 }}>✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Отправка медиа */}
            {showMedia && (
              <div style={{ background: '#fff', borderTop: '1px solid #e8e8e6', padding: '10px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>Отправить файл</div>
                <input ref={mediaRef} type="file" onChange={e => setMediaFile(e.target.files[0])} style={{ fontSize: 12, marginBottom: 8, display: 'block' }} />
                <input placeholder="Подпись (необязательно)" value={mediaCaption} onChange={e => setMediaCaption(e.target.value)} style={{ width: '100%', padding: '6px 10px', border: '1px solid #e8e8e6', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={sendMedia} disabled={!mediaFile || sending} style={{ padding: '6px 14px', background: '#534AB7', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                    {sending ? 'Отправка...' : '📎 Отправить'}
                  </button>
                  <button onClick={() => { setShowMedia(false); setMediaFile(null); }} style={{ padding: '6px 12px', background: 'none', border: '1px solid #e8e8e6', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#888' }}>Отмена</button>
                </div>
              </div>
            )}

            {/* Поле ввода */}
            <div style={{ padding: '10px 14px', background: '#fff', borderTop: '1px solid #e8e8e6', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <button onClick={() => { setShowQR(!showQR); setShowMedia(false); }} title="Быстрые ответы"
                style={{ padding: '8px 10px', border: '1px solid #e8e8e6', borderRadius: 8, background: showQR ? '#f0effe' : '#fff', cursor: 'pointer', fontSize: 16, flexShrink: 0, height: 40 }}>⚡</button>
              <button onClick={() => { setShowMedia(!showMedia); setShowQR(false); }} title="Отправить файл"
                style={{ padding: '8px 10px', border: '1px solid #e8e8e6', borderRadius: 8, background: showMedia ? '#f0effe' : '#fff', cursor: 'pointer', fontSize: 16, flexShrink: 0, height: 40 }}>📎</button>
              <textarea ref={textRef} value={text} onChange={e => setText(e.target.value)} onKeyDown={onKeyDown}
                placeholder="Сообщение... (Enter — отправить, Shift+Enter — перенос)"
                rows={2}
                style={{ flex: 1, padding: '8px 12px', border: '1px solid #e8e8e6', borderRadius: 10, fontSize: 14, resize: 'none', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }}
              />
              <button onClick={sendText} disabled={sending || !text.trim()}
                style={{ padding: '8px 16px', background: text.trim() ? '#534AB7' : '#e8e8e6', color: text.trim() ? '#fff' : '#aaa', border: 'none', borderRadius: 10, fontWeight: 500, fontSize: 14, cursor: text.trim() ? 'pointer' : 'default', flexShrink: 0, height: 40 }}>
                {sending ? '...' : '➤'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
